/**
 * Sticky add-to-cart bar — subscription price, mirrored from the main form.
 *
 * The sticky bar carries its own native <select name="selling_plan"> (the
 * combined Buy Once / delivery-frequency control), so the chosen plan submits
 * to /cart/add with no JS. Prices for each plan are pre-formatted server-side
 * into every option's data-was (one-time) / data-sale (discounted) attributes,
 * so nothing is money-formatted here.
 *
 * Behaviour:
 *   - Buy Once (value="")  -> one-time price only.
 *   - a delivery frequency -> struck one-time price + discounted (sale) price.
 *
 * The bar mirrors the MAIN product form's purchase option both ways:
 *   - main form changes  -> the sticky select + price follow it (and the right
 *     plan submits). This is what makes the subscription price appear on mobile,
 *     where the sticky bar's own dropdown is hidden.
 *   - sticky dropdown changes -> reflected back to the main form so the two
 *     never diverge.
 *
 * Scope: the main form lives in section[id^="MainProduct-"]; the quick-add
 * modal nests a second copy in .quick-add-modal__content-info. We only ever
 * read/drive the main one, never a quick-add form.
 *
 * Listeners are delegated on document because <variant-selects> re-renders the
 * entire <sticky-cart-modal> (innerHTML replaced) on every variant change,
 * which would drop a directly-bound listener.
 */
(function () {
  var SUB_SELECT = '.sticky-cart-modal .sticky-cart-sub-select';
  var MAIN_RADIO = '.rs-card__input';        // Buy Once value="", Subscribe value=planId
  var FREQ_SELECT = '.select-itemsss';        // delivery-frequency <select>, options carry data-id
  var MAIN_SCOPE = 'section[id^="MainProduct-"]';
  var QUICK_SCOPE = '.quick-add-modal__content-info';

  // Guards the main<->sticky round-trip so a programmatic change never bounces.
  var syncing = false;

  var defer = window.queueMicrotask
    ? window.queueMicrotask.bind(window)
    : function (fn) { Promise.resolve().then(fn); };

  // True when el belongs to the main product, not a nested quick-add modal.
  function inMainScope(el) {
    var scope = el.closest(QUICK_SCOPE + ',' + MAIN_SCOPE);
    return !!scope && scope.matches(MAIN_SCOPE);
  }

  // The main form's current selling plan: "" = Buy Once, a planId = subscription,
  // or null when the page has no Recharge widget (nothing to mirror).
  function mainSellingPlan() {
    var radios = document.querySelectorAll(MAIN_RADIO + ':checked');
    for (var i = 0; i < radios.length; i++) {
      if (inMainScope(radios[i])) return radios[i].value;
    }
    return document.querySelector(MAIN_RADIO) ? '' : null;
  }

  function mainRadio(target) {
    var radios = document.querySelectorAll(MAIN_RADIO + '[data-target="' + target + '"]');
    for (var i = 0; i < radios.length; i++) {
      if (inMainScope(radios[i])) return radios[i];
    }
    return null;
  }

  function mainFreqSelect() {
    var selects = document.querySelectorAll(FREQ_SELECT);
    for (var i = 0; i < selects.length; i++) {
      if (inMainScope(selects[i])) return selects[i];
    }
    return null;
  }

  // Re-render the price shown next to the title from the option's data attrs.
  // data-was is the struck price: the one-time price for a subscription, or the
  // compare-at (original) price for an on-sale Buy Once — both rendered with the
  // same .price__regular / .price__sale classes the PDP uses, so styling matches.
  function renderPrice(priceWrap, option) {
    var sale = option.getAttribute('data-sale') || '';
    var was = option.getAttribute('data-was');
    var isSub = !!option.value; // Buy Once carries value=""; subscriptions carry a plan id

    var html = '<div class="price">';
    if (was) html += '<div class="price__regular h4">' + was + '</div>';
    html += '<div class="price__container"><div class="price__sale h4">' + sale + '</div></div></div>';

    priceWrap.innerHTML = html;
    priceWrap.classList.toggle('sticky-cart__price--sub', isSub);
  }

  function swapStickyPrice(select) {
    var modal = select.closest('.sticky-cart-modal');
    var priceWrap = modal && modal.querySelector('.sticky-cart__price');
    var option = select.options[select.selectedIndex];
    if (priceWrap && option) renderPrice(priceWrap, option);
  }

  // Point every sticky select at planId (falling back to Buy Once when the plan
  // isn't offered) and refresh its price.
  function applyToSticky(planId) {
    document.querySelectorAll(SUB_SELECT).forEach(function (select) {
      var value = '';
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === planId) { value = planId; break; }
      }
      if (select.value !== value) select.value = value;
      swapStickyPrice(select);
    });
  }

  function syncStickyFromMain() {
    var planId = mainSellingPlan();
    if (planId === null) return; // no Recharge widget on this page
    applyToSticky(planId);
  }

  // Drive the main form from the sticky dropdown, then let custom.js sync the
  // main UI (price, badge, info panel). Guarded so the resulting main change
  // doesn't loop back into the sticky bar.
  function reflectToMain(planId) {
    if (!document.querySelector(MAIN_RADIO)) return;
    syncing = true;
    try {
      if (planId) {
        // Subscription: pick the matching frequency and let custom.js's
        // .select-itemsss handler set the Subscribe radio + label for us.
        var freq = mainFreqSelect();
        if (freq) {
          for (var i = 0; i < freq.options.length; i++) {
            if (String(freq.options[i].getAttribute('data-id')) === String(planId)) {
              freq.selectedIndex = i;
              break;
            }
          }
          freq.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          var sub = mainRadio('subscription');
          if (sub) {
            sub.value = planId;
            sub.checked = true;
            sub.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } else {
        var one = mainRadio('one-time');
        if (one) {
          one.checked = true;
          one.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } finally {
      syncing = false;
    }
  }

  // Main form -> sticky bar. Defer the read so custom.js (which also listens for
  // these events) has finished updating the Subscribe radio's value first.
  document.addEventListener('change', function (event) {
    var t = event.target;
    if (syncing || !t || typeof t.matches !== 'function') return;
    if (!t.matches(MAIN_RADIO) && !t.matches(FREQ_SELECT)) return;
    if (!inMainScope(t)) return; // ignore quick-add forms
    defer(syncStickyFromMain);
  });

  // Sticky dropdown -> swap its own price (native dispatchEvent uses this path
  // too) and reflect the choice back to the main form.
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || typeof select.matches !== 'function') return;
    if (!select.matches(SUB_SELECT)) return;
    swapStickyPrice(select);
    if (!syncing) reflectToMain(select.value);
  });

  // A variant change replaces the whole <sticky-cart-modal> innerHTML, resetting
  // the select to Buy Once — re-apply the main form's current plan afterwards.
  // Only react when the select itself is re-created, so our own price writes
  // (which mutate .sticky-cart__price) don't trigger a loop.
  function watchModal(modal) {
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches('.sticky-cart-sub-select') || node.querySelector('.sticky-cart-sub-select')) {
            defer(syncStickyFromMain);
            return;
          }
        }
      }
    });
    observer.observe(modal, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll('.sticky-cart-modal').forEach(watchModal);
    syncStickyFromMain();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
